import { useCallback, useEffect, useMemo, useState } from 'react'
import { getNotifications, markAllRead, markRead } from '../api/notifications'
import type { AppNotification } from '../api/notifications'
import { getFollowRequests, handleFollowRequest } from '../api/users'
import { useAuthStore } from '../store/auth'
import { usePageHeader } from '../hooks/usePageHeader'

export function Notifications() {
  const { username } = useAuthStore()
  const [notifications, setNotifications] = useState<AppNotification[] | null>(null)
  const [notificationsError, setNotificationsError] = useState('')

  useEffect(() => {
    getNotifications()
      .then(setNotifications)
      .catch(() => setNotificationsError('failed to load notifications'))
  }, [])

  const handleMarkAllRead = useCallback(async () => {
    await markAllRead().catch(() => {})
    setNotifications((prev) => prev ? prev.map((n) => ({ ...n, read: true })) : prev)
  }, [])

  const notifActions = useMemo(() => (
    (notifications?.filter((n) => !n.read).length ?? 0) > 0 ? (
      <button type="button" onClick={handleMarkAllRead} style={{ fontSize: '0.75rem', width: 'auto', opacity: 0.7 }}>
        mark all read
      </button>
    ) : null
  ), [notifications, handleMarkAllRead])

  usePageHeader('notifications', notifActions)

  async function respondToFollowRequest(notification: AppNotification, action: 'approve' | 'reject') {
    if (!username) return
    const reqId = notification.data.request_id as string
    await handleFollowRequest(username, reqId, action)
    await markRead(notification.id).catch(() => {})
    setNotifications((prev) => prev ? prev.filter((n) => n.id !== notification.id) : prev)
  }

  return (
    <div>
      {notificationsError && <p style={{ color: 'var(--color-1)', fontSize: '0.9rem' }}>{notificationsError}</p>}
      {notifications === null && !notificationsError && <p style={{ fontSize: '0.85rem', opacity: 0.6 }}>loading…</p>}
      {notifications !== null && notifications.length === 0 && <p style={{ fontSize: '0.85rem', opacity: 0.6 }}>no notifications</p>}
      {notifications && notifications.length > 0 && (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {notifications.map((n) => (
            <li
              key={n.id}
              style={{ padding: '0.75rem', marginBottom: '0.5rem', border: '1px solid var(--color-fg)', opacity: n.read ? 0.55 : 1 }}
            >
              {n.type === 'follow_request' ? (
                <FollowRequestNotification
                  notification={n}
                  onRespond={respondToFollowRequest}
                  onRead={() => {
                    markRead(n.id).catch(() => {})
                    setNotifications((prev) => prev ? prev.map((x) => x.id === n.id ? { ...x, read: true } : x) : prev)
                  }}
                />
              ) : (
                <span style={{ fontSize: '0.9rem' }}>{n.type}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function FollowRequestNotification({
  notification,
  onRespond,
  onRead,
}: {
  notification: AppNotification
  onRespond: (n: AppNotification, action: 'approve' | 'reject') => Promise<void>
  onRead: () => void
}) {
  const { username } = useAuthStore()
  const requesterUsername = notification.data.requester_username as string
  const [pending, setPending] = useState(false)
  const [done, setDone] = useState<'approve' | 'reject' | null>(null)
  const [gone, setGone] = useState(false)

  useEffect(() => {
    if (!username) return
    getFollowRequests(username)
      .then((reqs) => {
        const exists = reqs.some((r) => r.id === notification.data.request_id)
        if (!exists && !notification.read) onRead()
        setGone(!exists)
      })
      .catch(() => {})
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  async function respond(action: 'approve' | 'reject') {
    setPending(true)
    try {
      await onRespond(notification, action)
      setDone(action)
    } finally {
      setPending(false)
    }
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
      <span style={{ fontSize: '0.9rem' }}>
        ~{requesterUsername} wants to follow you
      </span>
      {done ? (
        <span style={{ fontSize: '0.8rem', opacity: 0.6 }}>{done}</span>
      ) : gone ? (
        <span style={{ fontSize: '0.8rem', opacity: 0.6 }}>already handled</span>
      ) : (
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            type="button"
            onClick={() => respond('approve')}
            disabled={pending}
            style={{ fontSize: '0.75rem', width: 'auto' }}
          >
            approve
          </button>
          <button
            type="button"
            onClick={() => respond('reject')}
            disabled={pending}
            style={{ fontSize: '0.75rem', width: 'auto', opacity: 0.7 }}
          >
            reject
          </button>
        </div>
      )}
    </div>
  )
}
